// src/utils/cache/userCache.js

const CACHE_KEY_PREFIX = 'userData_';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Gets cache key for specific user
 * @param {string} userId - User ID
 * @returns {string} Cache key
 */
const getCacheKey = (userId) => `${CACHE_KEY_PREFIX}${userId}`;

/**
 * Saves user data to localStorage with timestamp
 * @param {Object} userData - User data to cache
 * @param {string} userId - User ID
 */
export const saveUserCache = (userData, userId) => {
  try {
    const cacheData = {
      data: userData,
      timestamp: Date.now(),
      userId: userId // Store userId for validation
    };
    localStorage.setItem(getCacheKey(userId), JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to save user cache to localStorage:', error);
  }
};

/**
 * Retrieves user data from localStorage if valid
 * @param {string} userId - User ID
 * @returns {Object|null} Cached user data or null if expired/not found
 */
export const getUserCache = (userId) => {
  try {
    const cached = localStorage.getItem(getCacheKey(userId));
    if (!cached) return null;

    const { data, timestamp, userId: cachedUserId } = JSON.parse(cached);
    
    // Validate that this cache belongs to the current user
    if (cachedUserId !== userId) {
      console.warn('Cache user ID mismatch - clearing cache');
      clearUserCache(userId);
      return null;
    }
    
    // Check if cache is expired
    if (Date.now() - timestamp > CACHE_EXPIRY) {
      clearUserCache(userId);
      return null;
    }

    return data;
  } catch (error) {
    console.warn('Failed to retrieve user cache from localStorage:', error);
    return null;
  }
};

/**
 * Clears user data from localStorage
 * @param {string} userId - User ID (optional, clears all if not provided)
 */
export const clearUserCache = (userId = null) => {
  try {
    if (userId) {
      // Clear specific user's cache
      localStorage.removeItem(getCacheKey(userId));
    } else {
      // Clear all user caches
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.warn('Failed to clear user cache from localStorage:', error);
  }
};

/**
 * Checks if cache is valid (not expired)
 * @param {number} timestamp - Cache timestamp
 * @returns {boolean} True if cache is valid
 */
export const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_EXPIRY;
};

/**
 * Gets cache age in minutes
 * @param {number} timestamp - Cache timestamp
 * @returns {number} Age in minutes
 */
export const getCacheAge = (timestamp) => {
  if (!timestamp) return Infinity;
  return Math.floor((Date.now() - timestamp) / (1000 * 60));
};
